import { useCallback, useEffect, useRef, useState } from "react";
import { EcoSectionCarouselProps } from "../index";

export const useAnimationNavigationRight = (
  autoplay: EcoSectionCarouselProps['autoplay'],
  swiperAutoPlay: boolean
) => {
  const [progress, setProgress] = useState(0);
  const animationRef = useRef<number | null>(null);
  const startTimeRef = useRef(0);
  const duration = autoplay?.delay ?? 3000;
  const enabled = swiperAutoPlay && autoplay?.animationNavigationRight;

  const animate = useCallback((now: number) => {
    const elapsed = now - startTimeRef.current;
    setProgress(Math.min((elapsed / duration) * 100, 100));
    elapsed < duration && (animationRef.current = requestAnimationFrame(animate));
  }, [duration]);

  const start = useCallback(() => {
    animationRef.current && cancelAnimationFrame(animationRef.current);
    startTimeRef.current = performance.now();
    animationRef.current = requestAnimationFrame(animate);
  }, [animate]);

  const reset = useCallback(() => {
    animationRef.current && cancelAnimationFrame(animationRef.current);
    setProgress(0);
    enabled && start();
  }, [enabled, start]);

  useEffect(() => {
    const handleVisibility = () => {
      if (document.hidden) {
        animationRef.current && cancelAnimationFrame(animationRef.current);
      } else if (animationRef.current !== null) {
        reset();
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      animationRef.current && cancelAnimationFrame(animationRef.current);
    };
  }, [reset]);

  useEffect(() => {
    enabled ? start() : (animationRef.current && cancelAnimationFrame(animationRef.current));
    return () => { animationRef.current && cancelAnimationFrame(animationRef.current); };
  }, [enabled, start]);

  return { progress, resetProgress: reset };
};